import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Star, ShoppingCart, Package, Weight, Scissors, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { products, productReviews } from '../mockData';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { getProductPrice, calculateItemTotal } from '../utils/priceUtils';
import { trackViewContent, trackAddToCart } from "../utils/Pixel";
const ProductDetail = () => {
  const { id } = useParams();
  const { addToCart } = useCart();
  const { 
    t, 
    getProductName, 
    getCategoryName, 
    getCutType, 
    getPackagingOption, 
    getDescription,
    getWeight,
    getSpecKey,
    getSpecValue,
    formatLocalPrice,
    language, 
    isRTL 
  } = useLanguage();
  
  const product = products.find(p => p.id === parseInt(id));
  useEffect(() => {
  if (!product) return;

  trackViewContent({
    content_ids: [String(product.id)],
    content_name: product.name?.en || product.name || "Product",
    value: Number(product.price) || 0,
    currency: "EGP",
  });
}, [product]);

  const reviews = productReviews[id] || [];

  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedWeight, setSelectedWeight] = useState('');
  const [selectedCut, setSelectedCut] = useState('');
  const [selectedPackaging, setSelectedPackaging] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [currentPrice, setCurrentPrice] = useState(0);
  
  // Review form state
  const [reviewForm, setReviewForm] = useState({
    name: '',
    rating: 5,
    comment: ''
  });

  // Update price when weight selection changes
  useEffect(() => {
    if (product && selectedWeight) {
      const price = getProductPrice(product, selectedWeight);
      setCurrentPrice(price);
    }
  }, [product, selectedWeight]);

  if (!product) {
    return (
      <div className="min-h-screen bg-stone-50 py-20" dir={isRTL ? 'rtl' : 'ltr'}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('productNotFound')}</h2>
          <Button asChild>
            <Link to="/products">{t('backToProducts')}</Link>
          </Button>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
  if (!product) return;

  const price = Number(currentPrice || product.price || 0);

  addToCart({
    ...product,
    selectedWeight,
    selectedCut,
    selectedPackaging,
    quantity,
    price,
  });

  // Meta Pixel AddToCart
  trackAddToCart({
    content_ids: [String(product.id)],
    content_name: product.name?.en || product.name || "Product",
    value: price,
    currency: "EGP",
  });
};


  const handleReviewSubmit = (e) => {
    e.preventDefault();
    toast.success(t('reviewSubmitted'), {
      description: t('thankYouFeedback')
    });
    setReviewForm({ name: '', rating: 5, comment: '' });
  };

  const averageRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  const totalPrice = calculateItemTotal(currentPrice, quantity);

  // Get display name based on language
  const productDisplayName = getProductName(product.name);
  const categoryDisplayName = getCategoryName(product.category);
  const descriptionDisplay = getDescription(product.description);

  // Format review count for display
  const formatReviewCount = (count) => {
    if (isRTL) {
      const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
      const arabicCount = String(count).replace(/[0-9]/g, (d) => arabicNumerals[parseInt(d)]);
      return `${arabicCount} ${count === 1 ? t('review') : t('reviews')}`;
    }
    return `${count} ${count === 1 ? t('review') : t('reviews')}`;
  };

  // Format rating for display
  const formatRating = (rating) => {
    if (isRTL) {
      const arabicNumerals = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
      return String(rating).replace(/[0-9]/g, (d) => arabicNumerals[parseInt(d)]);
    }
    return rating;
  };

  return (
    <div className="min-h-screen bg-white" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Breadcrumb */}
      <div className="bg-stone-50 py-4 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-red-600">{t('home')}</Link>
            <span>/</span>
            <Link to="/products" className="hover:text-red-600">{t('products')}</Link>
            <span>/</span>
            <span className="text-gray-900">{productDisplayName}</span>
          </div>
        </div>
      </div>

      {/* Product Details */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button asChild variant="outline" className="mb-6">
            <Link to="/products">
              <ArrowLeft className={isRTL ? 'ml-2 rotate-180' : 'mr-2'} size={18} />
              {t('backToProducts')}
            </Link>
          </Button>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Image Gallery */}
            <div>
              <div className="mb-4 rounded-lg overflow-hidden shadow-xl">
                <img
                  src={product.images[selectedImage]}
                  alt={productDisplayName}
                  className="w-full h-[500px] object-cover"
                />
              </div>
              {product.images.length > 1 && (
                <div className="grid grid-cols-4 gap-4">
                  {product.images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImage === index ? 'border-red-600' : 'border-gray-200'
                      }`}
                    >
                      <img
                        src={img}
                        alt={`${productDisplayName} ${index + 1}`}
                        className="w-full h-24 object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div>
              <div className="mb-4">
                <span className="inline-block bg-red-100 text-red-600 text-sm font-semibold px-3 py-1 rounded-full">
                  {categoryDisplayName}
                </span>
              </div>
              
              <h1 className="text-4xl font-bold text-gray-900 mb-4">{productDisplayName}</h1>
              
              {/* Rating */}
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      size={20}
                      className={star <= averageRating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
                    />
                  ))}
                </div>
                <span className="text-gray-600">
                  {formatRating(averageRating)} ({formatReviewCount(reviews.length)})
                </span>
              </div>

              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                {descriptionDisplay}
              </p>

              {/* Specifications */}
              <Card className="mb-8 border-stone-200">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <CheckCircle className="text-green-600" size={24} />
                    {t('productSpecifications')}
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(product.specifications).map(([key, value]) => (
                      <div key={key} className="flex justify-between border-b border-stone-100 pb-2">
                        <span className="text-gray-600">{getSpecKey(key)}:</span>
                        <span className="font-semibold text-gray-900">{getSpecValue(value)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Options Selection */}
              <div className="space-y-6 mb-8">
                {/* Weight Selection */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-3">
                    <Weight size={18} className="text-red-600" />
                    {t('selectWeight')} *
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {product.weightOptions.map((weight) => (
                      <button
                        key={weight}
                        onClick={() => setSelectedWeight(weight)}
                        className={`px-4 py-3 border-2 rounded-lg font-medium transition-all ${
                          selectedWeight === weight
                            ? 'border-red-600 bg-red-50 text-red-600'
                            : 'border-gray-200 hover:border-red-300'
                        }`}
                      >
                        <div className="text-sm">{getWeight(weight)}</div>
                        <div className="text-xs font-bold mt-1">
                          {formatLocalPrice(product.pricing[weight])} {t('egp')}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Cut Type Selection */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-3">
                    <Scissors size={18} className="text-red-600" />
                    {t('selectCutType')} *
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {product.cutTypes.map((cut) => (
                      <button
                        key={cut}
                        onClick={() => setSelectedCut(cut)}
                        className={`px-4 py-3 border-2 rounded-lg font-medium transition-all ${
                          selectedCut === cut
                            ? 'border-red-600 bg-red-50 text-red-600'
                            : 'border-gray-200 hover:border-red-300'
                        }`}
                      >
                        {getCutType(cut)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Packaging Selection */}
                <div>
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-3">
                    <Package size={18} className="text-red-600" />
                    {t('selectPackaging')} *
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {product.packagingOptions.map((pkg) => (
                      <button
                        key={pkg}
                        onClick={() => setSelectedPackaging(pkg)}
                        className={`px-4 py-3 border-2 rounded-lg font-medium transition-all ${
                          selectedPackaging === pkg
                            ? 'border-red-600 bg-red-50 text-red-600'
                            : 'border-gray-200 hover:border-red-300'
                        }`}
                      >
                        {getPackagingOption(pkg)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Quantity */}
                <div>
                  <label className="text-sm font-semibold text-gray-900 mb-3 block">
                    {t('quantity')}
                  </label>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-12 h-12 border-2 border-gray-300 rounded-lg hover:bg-gray-50 font-bold text-xl"
                    >
                      -
                    </button>
                    <span className="text-2xl font-bold w-16 text-center">
                      {isRTL ? String(quantity).replace(/[0-9]/g, (d) => ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'][parseInt(d)]) : quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-12 h-12 border-2 border-gray-300 rounded-lg hover:bg-gray-50 font-bold text-xl"
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>

              {/* Price Display */}
              {selectedWeight && (
                <Card className="mb-6 bg-stone-50 border-stone-200">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-gray-700 font-medium">{t('unitPrice')}:</span>
                      <span className="text-2xl font-bold text-gray-900">{formatLocalPrice(currentPrice)} {t('egp')}</span>
                    </div>
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-gray-700 font-medium">{t('quantity')}:</span>
                      <span className="text-2xl font-bold text-gray-900">
                        ×{isRTL ? String(quantity).replace(/[0-9]/g, (d) => ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'][parseInt(d)]) : quantity}
                      </span>
                    </div>
                    <div className="border-t border-stone-300 pt-4 mt-4">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-semibold text-gray-900">{t('totalPrice')}:</span>
                        <span className="text-3xl font-bold text-red-600">{formatLocalPrice(totalPrice)} {t('egp')}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Add to Cart Button */}
              <Button
                onClick={handleAddToCart}
                size="lg"
                className="w-full bg-red-600 hover:bg-red-700 text-white h-14 text-lg"
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className={isRTL ? 'ml-2' : 'mr-2'} size={24} />
                {t('addToCart')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">{t('customerReviewsTitle')}</h2>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Reviews List */}
            <div className="lg:col-span-2 space-y-6">
              {reviews.length > 0 ? (
                reviews.map((review) => (
                  <Card key={review.id} className="border-0 shadow-lg">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-gray-900">{review.customerName}</h3>
                            {review.verified && (
                              <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded">
                                {t('verifiedPurchase')}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">{review.date}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              size={16}
                              className={star <= review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-gray-700 leading-relaxed">{review.comment}</p>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Card className="border-0 shadow-lg">
                  <CardContent className="p-12 text-center">
                    <p className="text-gray-600">{t('noReviewsYet')}</p>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Review Form */}
            <div className="lg:col-span-1">
              <Card className="border-0 shadow-lg sticky top-24">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">{t('writeReview')}</h3>
                  <form onSubmit={handleReviewSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('yourName')} *
                      </label>
                      <Input
                        type="text"
                        required
                        value={reviewForm.name}
                        onChange={(e) => setReviewForm({ ...reviewForm, name: e.target.value })}
                        placeholder={t('enterYourName')}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('rating')} *
                      </label>
                      <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                          >
                            <Star
                              size={32}
                              className={star <= reviewForm.rating ? 'fill-yellow-400 text-yellow-400 cursor-pointer' : 'text-gray-300 cursor-pointer'}
                            />
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('yourReview')} *
                      </label>
                      <Textarea
                        required
                        value={reviewForm.comment}
                        onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                        placeholder={t('shareExperience')}
                        rows={5}
                      />
                    </div>

                    <Button type="submit" className="w-full bg-red-600 hover:bg-red-700 text-white">
                      {t('submitReview')}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
